import type { Shortcut, ShortcutScope } from './types';

/** Read `import.meta.env.DEV` defensively — in tests / SSR it can be missing. */
function isDev(): boolean {
  const env = (import.meta as ImportMeta).env as
    | Record<string, unknown>
    | undefined;
  return env?.DEV === true;
}

/**
 * Singleton registry mirroring the command-palette pattern. Active page
 * scopes are managed by `usePageScope`; the registry retains the entire
 * list and the provider filters at runtime.
 */
class ShortcutsRegistry {
  private shortcuts = new Map<string, Shortcut>();
  private activeScopes = new Set<string>();
  private listeners = new Set<() => void>();
  private snapshot: ReadonlyArray<Shortcut> = [];

  register(shortcut: Shortcut): void {
    if (this.shortcuts.has(shortcut.id) && isDev()) {
      console.warn(
        `[shortcuts] duplicate id "${shortcut.id}" — overwriting`,
      );
    }
    this.shortcuts.set(shortcut.id, shortcut);
    this.recomputeSnapshot();
  }

  unregister(id: string): void {
    if (this.shortcuts.delete(id)) this.recomputeSnapshot();
  }

  list = (scope?: ShortcutScope | 'all'): ReadonlyArray<Shortcut> => {
    if (!scope || scope === 'all') return this.snapshot;
    return this.snapshot.filter((s) => (s.scope ?? 'global') === scope);
  };

  /** Return the shortcuts that are *currently* eligible to fire. */
  listActive = (): ReadonlyArray<Shortcut> => {
    return this.snapshot.filter((s) => {
      const scope = s.scope ?? 'global';
      if (scope === 'global') return true;
      return this.activeScopes.has(scope);
    });
  };

  activateScope(scope: string): void {
    this.activeScopes.add(scope);
    this.notify();
  }

  deactivateScope(scope: string): void {
    if (this.activeScopes.delete(scope)) this.notify();
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /** Test-only. */
  __resetForTests(): void {
    this.shortcuts.clear();
    this.activeScopes.clear();
    this.recomputeSnapshot();
  }

  private recomputeSnapshot(): void {
    this.snapshot = Array.from(this.shortcuts.values());
    this.notify();
  }

  private notify(): void {
    this.listeners.forEach((listener) => {
      listener();
    });
  }
}

export const shortcutsRegistry = new ShortcutsRegistry();
